import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Drawer, Empty, Segmented, Space, Spin, Tabs, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import { CoverSwapLists } from '../pages/ShiftBoardPage';
import { AvailabilityPanel } from '../pages/AvailabilityPage';
import { WFMSkillsPanel } from './WFMSkillsDrawer';

// Schedule Break & Lunch: click a person's name -> one panel about them, by
// tab: who could cover their shift that day (WFM Shift Swap Board access, and
// only when they are scheduled), their WFM Availability and their WFM Skills
// (those who can view WFM Skills).
export default function SchedulePersonDrawer({ agent, date, shifts, canCover, canWfm, onClose }) {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    if (agent && canWfm && !prefs) {
      api.get('/api/availability/preferences').then((r) => setPrefs(r.data || [])).catch(() => setPrefs([]));
    }
  }, [agent, canWfm, prefs]);
  const cover = canCover && (shifts || []).length > 0;
  const items = [
    ...(cover ? [{ key: 'cover', label: 'Who could cover this shift?',
      children: <WhoCouldCover agent={agent} date={date} shifts={shifts} /> }] : []),
    ...(canWfm ? [
      { key: 'avail', label: 'WFM Availability',
        children: prefs ? <AvailabilityPanel userId={agent?.ID} prefs={prefs} /> : <Spin /> },
      { key: 'skills', label: 'WFM Skills', children: <WFMSkillsPanel userId={agent?.ID} /> },
    ] : []),
  ];
  return (
    <Drawer open={!!agent} onClose={onClose} width={1100} destroyOnClose
      title={agent ? `${agent.DisplayName} · ${date.format('ddd MM/DD/YYYY')}` : ''}>
      {agent && <Tabs items={items} destroyInactiveTabPane={false} />}
    </Drawer>
  );
}

// Who could cover their shift that day (all of it, or part of it) if they
// were out: the WFM Shift Swap Board's lists, worked out for an absence that
// is never submitted (/api/shift-board/what-if); nothing is saved.
function WhoCouldCover({ agent, date, shifts }) {
  const [mode, setMode] = useState('entire');
  const [range, setRange] = useState(null);
  const [row, setRow] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [allSkills, setAllSkills] = useState(true);
  const [hideLeave, setHideLeave] = useState(true);

  // A new person: back to the entire shift, the part defaulting to their shift.
  useEffect(() => {
    setMode('entire');
    const first = (shifts || [])[0];
    setRange(first ? [dayjs(`2000-01-01T${first.start}`), dayjs(`2000-01-01T${first.end}`)] : null);
  }, [agent, shifts]);

  // Only a change that matters reloads: the part's times count only in Part.
  const part = mode === 'part' && range?.[0] && range?.[1]
    ? `${range[0].format('HH:mm')}-${range[1].format('HH:mm')}` : '';
  useEffect(() => {
    if (!agent) return;
    if (mode === 'part' && !part) return;
    const params = { user_id: agent.ID, date: date.format('YYYY-MM-DD') };
    if (mode === 'part') [params.start, params.end] = part.split('-');
    setLoading(true);
    setError(null);
    api.get('/api/shift-board/what-if', { params })
      .then((r) => setRow(r.data.row))
      .catch((err) => { setRow(null); setError(err.response?.data?.error || 'Could not work out who could cover.'); })
      .finally(() => setLoading(false));
  }, [agent, date, mode, part]);

  const fmt = (iso) => dayjs(iso).format('h:mm A');
  return (
    <>
      <Space wrap align="center" style={{ marginBottom: 12 }}>
        <Segmented value={mode} onChange={setMode}
          options={[{ value: 'entire', label: 'Entire shift' }, { value: 'part', label: 'Part of the shift' }]} />
        {mode === 'part' && (
          <TimePicker.RangePicker value={range} onChange={setRange} format="h:mm A" minuteStep={15} use12Hours
            allowClear={false} order={false} />
        )}
        <Checkbox checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)}>Only matching availability</Checkbox>
        <Checkbox checked={allSkills} onChange={(e) => setAllSkills(e.target.checked)}>Only matching all skills</Checkbox>
        <Checkbox checked={hideLeave} onChange={(e) => setHideLeave(e.target.checked)}>Hide people on leave</Checkbox>
      </Space>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Spin spinning={loading}>
        {!row || !row.Shifts.length ? (
          !loading && !error && <Empty description={mode === 'part' ? 'No shift in that time.' : 'Not scheduled that day.'} />
        ) : row.Shifts.map((s) => (
          <div key={s.Start} style={{ marginBottom: 20 }}>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              {fmt(s.Start)} – {fmt(s.End)} · {s.Department}
              <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal', marginLeft: 8 }}>
                {s.RequiredSkills.length ? `Skills needed: ${s.RequiredSkills.join(', ')}` : 'No skills needed'}
              </Typography.Text>
            </Typography.Title>
            <CoverSwapLists a={row} s={s} onlyAvailable={onlyAvailable} allSkills={allSkills} hideLeave={hideLeave} />
          </div>
        ))}
      </Spin>
    </>
  );
}
